import { isSameDay, parseISO } from "date-fns";
import { type TFunction } from "i18next";

import { formatNumber, getLastOfferUpdateDatetime } from "../../../../utils";
import { getLocalisedName } from "../../../../utils/i18n";
import { type Deposit } from "../../../Deposits/DepositCard/deposit-dtos";
import { formatDepositDate } from "../../../Deposits/utils";
import {
  offerCommodityTitle,
  offerCurrencySymbol,
} from "../../../RepresentativeOffers/components/OfferCard";
import {
  type Offer,
  OfferStatus,
} from "../../../RepresentativeOffers/components/OfferCard/interfaces";
import {
  formatQuantityRange,
  formatUnitPrice,
  getActiveProposal,
  isProposalFromTrader,
  offerPictureUrls,
  offerDisplayStatus,
  offerTerms,
} from "../../../RepresentativeOffers/utils";
import { type HighlightCardData } from "./types";

const KEY = "home.manager.overview.highlights";

const withUnit = (value: string, unit?: string) => (unit ? `${value} ${unit}` : value);

const formatHighlightDate = (date: string, t: TFunction, now: Date) =>
  formatDepositDate(
    date,
    {
      today: t("farmersMarket.depositCard.relativeDate.today"),
      yesterday: t("farmersMarket.depositCard.relativeDate.yesterday"),
    },
    now
  );

export const depositToHighlight = (
  deposit: Deposit,
  t: TFunction,
  now: Date = new Date()
): HighlightCardData => {
  const unit = deposit.unit_of_measure?.code;
  const currency = offerCurrencySymbol(t);
  const commodity = getLocalisedName(deposit.farmer_produce?.commodity || deposit.commodity);
  const grade = deposit.quality_grade?.name || deposit.quality;

  return {
    id: deposit.id,
    imageUrl: deposit.picture_urls?.[0],
    badge: isSameDay(parseISO(deposit.backend_creation_time), now)
      ? { label: t(`${KEY}.collectedToday`), variant: "success" }
      : undefined,
    location: deposit.aggregator?.location?.name,
    title: grade ? `${commodity} · ${grade}` : commodity,
    subtitle: deposit.aggregator?.name ?? "",
    date: t("farmersMarket.depositCard.listed", {
      date: formatHighlightDate(deposit.backend_creation_time, t, now),
    }),
    metrics: [
      { label: t(`${KEY}.quantity`), value: withUnit(formatNumber(deposit.quantity), unit) },
      {
        label: t("farmersMarket.depositCard.unitPrice"),
        value: formatUnitPrice(deposit.price, currency, unit ?? ""),
      },
      {
        label: t("farmersMarket.depositCard.totalValue"),
        value: `${currency}${formatNumber(Number(deposit.quantity) * Number(deposit.price))}`,
      },
    ],
  };
};

export const waitingOn = (offer: Offer): string | undefined => {
  const aggregator = offer.tagged_aggregator?.name;
  const buyer = offer.trader?.name;

  switch (offer.status) {
    case OfferStatus.New:
    case OfferStatus.ExchangeDetailsDisputed:
      return aggregator;
    case OfferStatus.Negotiating:
      return isProposalFromTrader(offer.latest_negotiation_proposal) ? aggregator : buyer;
    case OfferStatus.ExchangeDetailsUpdated:
    case OfferStatus.ExchangePaymentPending:
      return buyer;
    default:
      return undefined;
  }
};

export const offerToHighlight = (
  offer: Offer,
  t: TFunction,
  now: Date = new Date()
): HighlightCardData => {
  const unit = offer.unit_of_measure?.code;
  const currency = offerCurrencySymbol(t);
  const terms = offerTerms(offer);
  const waitingName = waitingOn(offer);
  const displayStatus = offerDisplayStatus(offer, true);

  return {
    id: offer.id,
    imageUrl: offerPictureUrls(offer)[0],
    badge: {
      label: t(displayStatus.labelKey, displayStatus.params),
      variant: displayStatus.variant,
    },
    title: offerCommodityTitle(offer),
    subtitle: [offer.trader?.name, offer.tagged_aggregator?.name].filter(Boolean).join(" → "),
    date: t("representativeOffers.card.updated", {
      date: formatHighlightDate(
        getLastOfferUpdateDatetime(offer, getActiveProposal(offer)),
        t,
        now
      ),
    }),
    metrics: [
      {
        label: t(`${KEY}.quantity`),
        value: withUnit(formatQuantityRange(terms.minimumQuantity, terms.maximumQuantity), unit),
      },
      {
        label: t("farmersMarket.depositCard.unitPrice"),
        value: formatUnitPrice(terms.price, currency, unit ?? ""),
      },
      {
        label: t("representativeOffers.card.offerValue"),
        value: `${currency}${formatQuantityRange(
          terms.price * terms.minimumQuantity + terms.deliveryCost,
          terms.price * terms.maximumQuantity + terms.deliveryCost
        )}`,
      },
    ],
    footer: waitingName ? t(`${KEY}.waitingOn`, { name: waitingName }) : undefined,
  };
};
